import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { messaging } from "../firebase-config";
import { onMessage } from "firebase/messaging";
import { useGlobalAlert } from "./AlertContext";
import { useNavigate } from "react-router-dom";

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [newNotification, setNewNotification] = useState(null);
  const { triggerAlert } = useGlobalAlert();
  const navigate = useNavigate();

  // Shared handler for both Firebase foreground and SW background messages
  const handleIncomingNotification = useCallback(
    (payload) => {
      setUnreadCount((prev) => prev + 1);

      // Normalize payload to match the database structure for UI rendering
      const incomingNotif = {
        id: payload?.data?.notification_id || Date.now().toString(),
        title:
          payload?.notification?.title ||
          payload?.data?.title ||
          "New Notification",
        message: payload?.notification?.body || payload?.data?.body || "",
        is_read: false,
        created_at: new Date().toISOString(),
      };

      setNewNotification(incomingNotif);

      // DYNAMIC ROUTING LOGIC (Foreground parity with sw.js)
      const category = payload?.data?.category?.toUpperCase() || "";
      const notifId = payload?.data?.notification_id || "";
      let routePath = payload?.data?.url || "/notifications";

      if (category === "TREAT") {
        routePath = "/receive-a-treat";
      } else if (category === "WALK_REQUEST") {
        routePath = "/receive-a-book";
      }

      if (notifId) {
        routePath += `?notificationId=${notifId}`;
      }

      // Pass the navigation handler as the 3rd argument
      triggerAlert(
        `${incomingNotif.title}: ${incomingNotif.message}`,
        "info",
        () => navigate(routePath),
      );
    },
    [triggerAlert, navigate],
  );

  useEffect(() => {
    let unsubscribeFCM = () => {};

    // Listen via Firebase's native onMessage (Foreground)
    if (messaging) {
      unsubscribeFCM = onMessage(messaging, (payload) => {
        handleIncomingNotification(payload);
      });
    }

    // Listen via Service Worker postMessage (Background / Push Intercept)
    const handleSWMessage = (event) => {
      if (event.data && event.data.type === "NEW_PUSH_NOTIFICATION") {
        handleIncomingNotification(event.data.payload);
      }
    };

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", handleSWMessage);
    }

    return () => {
      unsubscribeFCM();
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener("message", handleSWMessage);
      }
    };
  }, [handleIncomingNotification]);

  const clearUnreadCount = () => setUnreadCount(0);

  return (
    <NotificationContext.Provider
      value={{ unreadCount, setUnreadCount, clearUnreadCount, newNotification }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
